import { isBefore, sub } from 'date-fns';
import * as yup from 'yup';

const phoneNumberRegex = /^(?:(?:(?:00\s?|\+)44\s?|0)(?:[123678]\d{8,9}))$/;
const dayRegex = /\b(0?[1-9]|[1-2][0-9]|[3][0-1])\b/;
const monthRegex = /^(?:0?[1-9]|1[012])$/;

const AddressSchema = yup.object().shape({
  house_name: yup.string(),
  house_number: yup.string(),
  street_line_1: yup.string(),
  street_line_2: yup.string(),
  town: yup.string(),
  postcode: yup.string().required('Home address required'),
  equifax_token: yup.string(),
});

export const schema = yup.object().shape({
  firstName: yup.string().required('Director name required'),
  lastName: yup.string().required('Director name required'),
  isDirector: yup.boolean(),
  dobDay: yup
    .string()
    .required('Day required')
    .matches(dayRegex, 'Enter a valid day'),
  dobMonth: yup
    .string()
    .required('Month required')
    .matches(monthRegex, 'Enter a valid month'),
  dobYear: yup
    .string()
    .min(4, 'Year required')
    .max(4, 'Invalid year')
    .required('Year required')
    .test(
      'Is 18 or over',
      'You must be 18 years or older',
      function (value, testContext) {
        if (
          !Number(testContext.parent.dobMonth) ||
          !Number(testContext.parent.dobDay)
        ) {
          // Don't check age unless day + month entered
          return true;
        }
        return isOver18(
          value,
          testContext.parent.dobMonth,
          testContext.parent.dobDay,
        );
      },
    ),
  residentialAddresses: yup.array(AddressSchema).required(),
  phoneNumber: yup
    .string()
    .required('Phone number required')
    .test(
      'Is a valid number',
      'Please use a valid phone number format',
      function (value) {
        return isValidNumber(value);
      },
    ),
  privacyPolicyAgreement: yup
    .boolean()
    .required('Privacy policy is required')
    .oneOf([true], 'The terms and conditions must be accepted.'),
});

export const minimumPersonalDataSchema = yup.object().shape({
  firstName: yup.string().required('Director name required'),
  lastName: yup.string().required('Director name required'),
  isDirector: yup.boolean(),
  dobDay: yup
    .string()
    .required('Day required')
    .matches(dayRegex, 'Enter a valid day'),
  dobMonth: yup
    .string()
    .required('Month required')
    .matches(monthRegex, 'Enter a valid month'),
  dobYear: yup
    .string()
    .min(4, 'Year required')
    .max(4, 'Invalid year')
    .required('Year required')
    .test(
      'Is 18 or over',
      'You must be 18 years or older',
      function (value, testContext) {
        if (
          !Number(testContext.parent.dobMonth) ||
          !Number(testContext.parent.dobDay)
        ) {
          // Don't check age unless day + month entered
          return true;
        }
        return isOver18(
          value,
          testContext.parent.dobMonth,
          testContext.parent.dobDay,
        );
      },
    ),
  residentialAddresses: yup.array(AddressSchema).required(),
  phoneNumber: yup
    .string()
    .required('Phone number required')
    .test(
      'Is a valid number',
      'Please use a valid phone number format',
      function (value) {
        return isValidNumber(value);
      },
    ),
  privacyPolicyAgreement: yup
    .boolean()
    .required('Privacy policy is required')
    .oneOf([true], 'The terms and conditions must be accepted.'),
});

const isOver18 = (year: string, month: string, day: string) => {
  const date18YearsAgo = sub(new Date(), { years: 18 });

  return isBefore(
    new Date(parseInt(year), parseInt(month), parseInt(day)),
    date18YearsAgo,
  );
};

const isValidNumber = (value: string | undefined) => {
  if (!value) {
    return true;
  }

  const whitespaceRemoved = value?.replace(/\s/g, '');
  return phoneNumberRegex.test(whitespaceRemoved);
};
